import { ActionModal } from '@kcftech/design-system';
import {
    AssemblyApplicationCreateRequest,
    IAssemblyApplicationResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    AssemblyApplicationFormContent,
    AssemblyApplicationFormData,
} from 'pages/Settings/AssemblyType/AssemblyApplicationFormContent';
import { useForm } from 'react-hook-form';

export type ApplicationCreationInfo = Readonly<{
    siblings: IAssemblyApplicationResponse[];
}>;

export type CreateAssemblyApplicationProps = ApplicationCreationInfo &
    Readonly<{
        assemblyTypeId: number;
        onClose: (createdApplication: IAssemblyApplicationResponse | undefined) => void;
    }>;

export function CreateAssemblyApplicationModal({
    assemblyTypeId,
    siblings,
    onClose,
}: CreateAssemblyApplicationProps) {
    const siblingNames = siblings.map((sibling) => sibling.Name);
    const siblingCodes = siblings.map((sibling) => sibling.Code);

    const { showToast } = useNotificationContext();
    const form = useForm<AssemblyApplicationFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: '',
            code: '',
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
    } = form;

    const { mutate: createApplication, isPending } = useMutation({
        mutationFn: async (formData: AssemblyApplicationFormData) =>
            sdClient.createAssemblyApplication({
                body: new AssemblyApplicationCreateRequest({
                    TypeId: assemblyTypeId,
                    Name: formData.name.trim(),
                    Code: formData.code.trim(),
                }),
            }),

        onSuccess: (result) => {
            onClose(result);
            showToast({
                type: 'success',
                content: 'Successfully created assembly application',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to create assembly application',
            }),
    });

    const onSave = handleSubmit((formData) => createApplication(formData));

    return (
        <ActionModal
            onAction={onSave}
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isPending)
            }
            onClose={() => onClose(undefined)}
            title="Create Assembly Application"
            height={350}
            actionButtonText="Create"
            closeButtonText="Cancel"
        >
            <AssemblyApplicationFormContent
                form={form}
                siblingNames={siblingNames}
                siblingCodes={siblingCodes}
            />
        </ActionModal>
    );
}
